import { Box, Grid, MenuItem, Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { theme } from '../../../../theme'
import { validators } from '../../../../validation/validators'
import { powerPerAreaUnit, temperatureUnit } from '../../../../util/formatters'
import {
  EnergySystemType,
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../../generated/graphql'
import {
  InputTabFormValues,
  saveInputData,
  updateModel,
  useInputTabStyles,
} from './input-util'

type Props = {
  id: string
  schedules: ScheduleFragment[]
  defaultValues: InputTabFormValues | undefined
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputHeatLoadTab({
  id,
  schedules,
  defaultValues,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const classes = useInputTabStyles()
  const { t } = useTranslation()

  const { control, errors, reset, formState } = useForm<InputTabFormValues>({
    defaultValues,
    mode: 'onBlur',
  })

  const dbUpdateNeededRef = useRef(false)

  const updateModelData = async (values: InputTabFormValues) => {
    await updateModel({
      values,
      defaultValues: defaultValues ?? ({} as InputTabFormValues),
      esType: EnergySystemType.HeatLoad,
      control,
      formState,
      updateStateRef: dbUpdateNeededRef,
      onReset: reset,
      onUpdate,
    })
  }

  const saveChanges = async () => {
    await saveInputData({ energySystemId: id, control, onDbUpdate })
  }

  useEffect(() => {
    void updateModelData(defaultValues ?? ({} as InputTabFormValues))

    return () => {
      if (dbUpdateNeededRef.current && defaultValues) {
        void saveChanges()
        dbUpdateNeededRef.current = false
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form style={{ marginTop: theme.spacing(2) }}>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.heatLoad.occupants')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.occupantHeatLoad"
                  label={t('energySystem.parameters.power')}
                  error={errors.values?.parameters?.heatLoad?.occupantHeatLoad}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{powerPerAreaUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.occupantScheduleId"
                  label={t('energySystem.parameters.heatLoad.schedule')}
                  error={
                    errors.values?.parameters?.heatLoad?.occupantScheduleId
                  }
                  rules={validators.required}
                  size="small"
                  disabled
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.occupantHeatLoadUtilFactor"
                  label={t(
                    'energySystem.parameters.heatLoad.heatLoadUtilizationFactor',
                  )}
                  error={
                    errors.values?.parameters?.heatLoad
                      ?.occupantHeatLoadUtilFactor
                  }
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.heatLoad.appliances')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.applianceHeatLoad"
                  label={t('energySystem.parameters.power')}
                  error={errors.values?.parameters?.heatLoad?.applianceHeatLoad}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{powerPerAreaUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.applianceScheduleId"
                  label={t('energySystem.parameters.heatLoad.schedule')}
                  error={
                    errors.values?.parameters?.heatLoad?.applianceScheduleId
                  }
                  rules={validators.required}
                  size="small"
                  disabled
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.applianceHeatLoadUtilFactor"
                  label={t(
                    'energySystem.parameters.heatLoad.heatLoadUtilizationFactor',
                  )}
                  error={
                    errors.values?.parameters?.heatLoad
                      ?.applianceHeatLoadUtilFactor
                  }
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.heatLoad.lighting')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.lightingHeatLoad"
                  label={t('energySystem.parameters.power')}
                  error={errors.values?.parameters?.heatLoad?.lightingHeatLoad}
                  rules={validators.decimal}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{powerPerAreaUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.lightingScheduleId"
                  label={t('energySystem.parameters.heatLoad.schedule')}
                  error={
                    errors.values?.parameters?.heatLoad?.lightingScheduleId
                  }
                  rules={validators.required}
                  size="small"
                  disabled
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.lightingHeatLoadUtilFactor"
                  label={t(
                    'energySystem.parameters.heatLoad.heatLoadUtilizationFactor',
                  )}
                  error={
                    errors.values?.parameters?.heatLoad
                      ?.lightingHeatLoadUtilFactor
                  }
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.heatLoad.outsideTemps')}
            </Typography>
            <Grid container spacing={1} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  {t(
                    'energySystem.parameters.heatLoad.heatLoadUtilStartTempLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.heatLoadUtilStartTemp"
                  error={
                    errors.values?.parameters?.heatLoad?.heatLoadUtilStartTemp
                  }
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  style={{ marginTop: theme.spacing(2) }}
                >
                  {t(
                    'energySystem.parameters.heatLoad.affectingSpaceHeatingStartTempLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.heatLoad.affectingSpaceHeatingStartTemp"
                  error={
                    errors.values?.parameters?.heatLoad
                      ?.affectingSpaceHeatingStartTemp
                  }
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </form>
  )
}
